/**
 * AI conversation persistence, inside the CURRENT ACCOUNT'S data root
 * (Tauri only, 18.08.2026).
 *
 *   <AppLocalData>/users/<accountId>/temp/ai/conversations/{workspaceId}.json
 *
 * Panel width and the two mode toggles already survived a restart; the
 * conversation did not, so closing the app — or merely closing the panel, since
 * App unmounts it — threw the thread away with no history and no way back.
 *
 * ── The privacy obligation, and why it is not a handler ──────────────────────
 *
 * A transcript is not just what the user typed. It carries tool RESULTS:
 * command output from their servers, document text, database schemas, response
 * bodies. Persisting that turns a session-only exposure into an at-rest one,
 * and the wrong way to answer it is a "clear on logout" listener that someone
 * has to remember to keep correct.
 *
 * So these files live under `accountPath`, and sign-out deletes that entire
 * root (accountData.purgeAccountData, called from authStore). Cleanup is a
 * property of WHERE the data is, not of a handler that could be missed — the
 * same reasoning historyResponseStore.js and workspaceMirror.js already use.
 *
 * ── Why both shapes are stored ───────────────────────────────────────────────
 *
 * `messages` is what the transcript renders. `anthropicHistory` is what the
 * MODEL sees, and they are not the same shape: the second holds tool_use and
 * tool_result blocks the first renders as cards. Restoring only the visible one
 * gives an assistant with amnesia about its own tool calls — it would see a
 * user asking about results it has no record of producing.
 *
 * Scoped per workspace. Restoring one workspace's thread into another is a
 * data-leak-shaped bug in a product with teams.
 */
import { isTauri } from '../../utils/tauri';
import { accountPath } from '../accountData';

const ROOT_REL = 'temp/ai/conversations';

/** Threads kept on disk. Oldest are pruned when a new one pushes past this. */
export const MAX_THREADS = 20;

/**
 * Hard ceiling on a persisted thread.
 *
 * A conversation grows without bound and a tool result can be large. This is
 * not about disk — it is that reading a 40 MB transcript at startup stalls the
 * panel, and nobody scrolls back that far. Newest messages are kept.
 */
export const MAX_PERSISTED_MESSAGES = 200;

let fsAvailableCache;

async function fsModule() {
  return import('@tauri-apps/plugin-fs');
}

async function rootDir() {
  return accountPath(ROOT_REL);
}

async function ensureDir() {
  const dir = await rootDir();
  if (!dir) throw new Error('no account data dir');
  const fs = await fsModule();
  await fs.mkdir(dir, { recursive: true });
  return dir;
}

/** True when a thread can be written at all (Tauri + a writable account dir). */
export async function canPersistConversations() {
  if (fsAvailableCache !== undefined) return fsAvailableCache;
  if (!isTauri()) {
    fsAvailableCache = false;
    return false;
  }
  try {
    await ensureDir();
    fsAvailableCache = true;
  } catch (err) {
    console.warn('[conversationStore] account storage unavailable:', err);
    fsAvailableCache = false;
  }
  return fsAvailableCache;
}

/** Test/lifecycle hook — the account root can change under us. */
export function resetConversationStoreCache() {
  fsAvailableCache = undefined;
}

/**
 * A workspace id as a filename.
 *
 * Ids come from a server and from local generation, so this never trusts one
 * into a path: anything outside the safe set becomes an underscore. A bare `..`
 * would otherwise address the parent directory.
 */
function fileNameFor(workspaceId) {
  const id = String(workspaceId ?? '').trim();
  if (!id) return null;
  const safe = id.replace(/[^A-Za-z0-9_-]/g, '_');
  return safe && safe !== '.' && safe !== '..' ? `${safe}.json` : null;
}

/** Keep the newest N, because the tail is what a reader is looking at. */
function capMessages(messages) {
  const list = Array.isArray(messages) ? messages : [];
  return list.length > MAX_PERSISTED_MESSAGES ? list.slice(-MAX_PERSISTED_MESSAGES) : list;
}

/**
 * Persist one workspace's thread.
 *
 * Call on turn completion, never inside the streaming loop — a write per token
 * would hammer the disk for a file nobody reads until the next launch.
 */
export async function writeConversation(workspaceId, { messages, anthropicHistory }) {
  const name = fileNameFor(workspaceId);
  if (!name || !(await canPersistConversations())) return false;
  try {
    const fs = await fsModule();
    const dir = await ensureDir();
    const payload = JSON.stringify({
      version: 1,
      workspace_id: String(workspaceId),
      saved_at: Date.now(),
      messages: capMessages(messages),
      anthropic_history: capMessages(anthropicHistory),
    });
    await fs.writeTextFile(`${dir}/${name}`, payload);
    await pruneThreads();
    return true;
  } catch (err) {
    // Persistence is a convenience; failing to save must never break a turn.
    console.warn('[conversationStore] could not save conversation:', err);
    return false;
  }
}

/** Read a workspace's thread back, or null when there is nothing usable. */
export async function readConversation(workspaceId) {
  const name = fileNameFor(workspaceId);
  if (!name || !(await canPersistConversations())) return null;
  try {
    const fs = await fsModule();
    const dir = await rootDir();
    const raw = await fs.readTextFile(`${dir}/${name}`);
    const parsed = JSON.parse(raw);
    // A file from another workspace under this name means the id was mangled
    // into a collision. Refuse it rather than restore someone else's thread.
    if (parsed?.workspace_id && String(parsed.workspace_id) !== String(workspaceId)) return null;
    return {
      messages: Array.isArray(parsed?.messages) ? parsed.messages : [],
      anthropicHistory: Array.isArray(parsed?.anthropic_history) ? parsed.anthropic_history : [],
      savedAt: Number(parsed?.saved_at) || null,
    };
  } catch {
    // Missing file is the normal first-run case; a corrupt one is not worth a
    // warning the user cannot act on. Either way: start fresh.
    return null;
  }
}

/** Forget one workspace's thread — the panel's Clear action. */
export async function clearConversation(workspaceId) {
  const name = fileNameFor(workspaceId);
  if (!name || !(await canPersistConversations())) return false;
  try {
    const fs = await fsModule();
    const dir = await rootDir();
    await fs.remove(`${dir}/${name}`);
    return true;
  } catch {
    return false;
  }
}

/**
 * Drop the oldest threads past MAX_THREADS.
 *
 * Bounded by mtime rather than by a manifest: a manifest is a second source of
 * truth that goes stale the moment a file is removed by anything else — and
 * sign-out removes the whole directory.
 */
export async function pruneThreads() {
  try {
    const fs = await fsModule();
    const dir = await rootDir();
    if (!dir) return;
    const entries = await fs.readDir(dir);
    const files = entries.filter((e) => e.isFile && e.name.endsWith('.json'));
    if (files.length <= MAX_THREADS) return;

    const stamped = await Promise.all(
      files.map(async (f) => {
        try {
          const stat = await fs.stat(`${dir}/${f.name}`);
          return { name: f.name, at: Number(stat?.mtime ?? 0) };
        } catch {
          return { name: f.name, at: 0 };
        }
      })
    );
    stamped.sort((a, b) => b.at - a.at);
    await Promise.all(
      stamped.slice(MAX_THREADS).map((f) => fs.remove(`${dir}/${f.name}`).catch(() => {}))
    );
  } catch {
    // Pruning is housekeeping — never let it fail a save.
  }
}
